import _extends from "@babel/runtime/helpers/extends";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _isNumber from "lodash/isNumber";
import _capitalize from "lodash/capitalize";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import classNames from 'classnames';
import { Type } from '@nerdwallet/currency';
import React, { Fragment, useContext } from 'react';
import { Impression, ImpressionContext } from '@nerdwallet/analytics/react/impression';
import LikelihoodOfApproval, { approvalOddsPopoverContent, getColorAndLabel, getCreditScoreComparisonContent, Heading, CREDIT_DASHBOARD_PATH_WITH_TRK } from '@nerdwallet/likelihood-of-approval';
import { getApprovalOdds, getLegacyId, getMaximumFICO, getMinimumFICO, getOngoingAPR, getSlug, getStarRating, getTermsAndFeesLink } from '@nerdwallet/marketplace/selectors/credit-cards';
import { getRecommendedCreditScoreFormattedDetails, getMaxCreditScoreBucket, getMinCreditScoreBucket } from '@nerdwallet/marketplace/utils/credit-cards';
import MarketplaceApplyNow, { APPLY_NOW_LINK_TYPES, APPLY_NOW_FINISH_TYPES, LINK_TYPES } from '@nerdwallet/react-marketplace-apply-now';
import classes from '@nerdwallet/base-styles/dist/classes';
import MarketplaceStarRating, { LAYOUTS, TOOLTIP_TEXT } from '@nerdwallet/react-marketplace-star-rating';
import { getMonetizable } from './utils';
import { FORMATS, VALUE_SIZES } from '../constants';
import styles from '../quick-facts.module.less';
/**
 * Preserve exports for backwards compatibility.
 * @deprecated - Leverage exports from `@nerdwallet/marketplace/drivers/credit-cards` directly.
 */

export { getDriverAnnualFee as getQuickFactAnnualFee, getDriverBalanceTransferFeeDescription as getQuickFactBalanceTransferFeeDescription, getDriverBonus as getQuickFactEstBonusValue, getDriverIntroApr as getQuickFactIntroApr, getDriverIntroOffer as getQuickFactIntroOffer, getDriverRecommendedCreditScoreRange as getQuickFactRecommendedCreditScoreRange, getDriverRewardRate as getQuickFactRewardRate, getDriverMinimumDepositDescription as getQuickFactMinimumDepositDescription } from '@nerdwallet/marketplace/drivers/credit-cards';
/**
 * Preserve exports for backwards compatibility.
 * @deprecated - Leverage export from `@nerdwallet/marketplace/utils/credit-cards` directly.
 */

export { getRecommendedCreditScoreFormattedDetails };
export var WAIVED_FIRST_YEAR_STRING = 'Waived first year';
export var DISCLAIMER_TEXT = 'But, heads up, being within the recommended range doesn’t guarantee approval. Credit score is only one factor lenders consider and they may not use the TransUnion Vantage Score like we do.';
export var TRANSUNION_LOGO_CTA = /*#__PURE__*/React.createElement("a", {
  href: CREDIT_DASHBOARD_PATH_WITH_TRK,
  target: "_",
  className: classes('display-block')
}, /*#__PURE__*/React.createElement("div", {
  className: classNames('tu-logo', styles.tuLogo)
}));
/**
 * A getter for credit score given identity object.
 *
 * @param {object} options
 * @param {object} options.identity
 */

export var getCreditScore = function getCreditScore(options) {
  return _get(options, 'identity.profiles.tu_credit_report_profile.vantage_score_3');
};
/**
 * Get the Likelihood of Approval quick fact
 *
 * @param   {Object}  offer                                   The CC offer.
 * @param   {Object}  options
 * @param   {Object}  options.approvalOddsEnabled             Flag to enabled or disable approvalOdds
 * @param   {Object}  options.identity                        The identity object typically found in state
 *                                                            This is used to determine if the user is logged in
 *                                                            as well as the user's credit score
 * @param   {node}    options.ctaComponent                    A custom CTA Component
 * @param   {Object}  options.trackingProps
 * @param   {string}  options.trackingProps.impressionKey     The product impression key
 * @param   {string}  options.trackingProps.sectionCategory   The section category
 * @param   {number}  options.trackingProps.sectionPosition   The section position
 *
 * @return  {Object}  The likelihood of approval quick fact
 */

export var getQuickFactLikelihoodOfApproval = function getQuickFactLikelihoodOfApproval(offer) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var creditScore = getCreditScore(options); // Generate props to pass to both the LOA and the LOA heading.

  var commonProps = _objectSpread({
    approvalOdds: options.approvalOddsEnabled ? getApprovalOdds(offer) : null,
    creditScore: creditScore,
    id: getSlug(offer),
    maxCreditScoreRange: getMaxCreditScoreBucket(offer),
    minCreditScoreRange: getMinCreditScoreBucket(offer)
  }, options);

  return {
    label: /*#__PURE__*/React.createElement(Heading, _extends({}, commonProps, {
      unstyled: true
    })),
    id: 'likelihoodOfApproval',
    value: !_isEmpty(offer) ? /*#__PURE__*/React.createElement(LikelihoodOfApproval, _extends({
      backgroundColor: "transparent",
      cardId: getLegacyId(offer),
      fullWidth: true,
      omitHeading: true,
      redirect_uri: "#",
      useTracking: _get(options.identity, 'isSignedIn', false)
    }, commonProps, options.trackingProps)) : null,
    valueFormat: FORMATS.UNFORMATTED,
    wide: true
  };
};
/**
 * @todo - Create Structured Content compatible value, tooltip and backport into `@nerdwallet/marketplace/drivers/credit-cards`.
 * @see {@link https://nerdwallet.atlassian.net/browse/RUR-171}
 *
 * Get the likelihood of approval text quick fact
 * This is for the Keto Card AB test to replace the `getQuickFactLikelihoodOfApproval`
 *
 * |-------------------------------------------------------------------------------------------|
 * |                  |  TU-activated          | Not TU-activated                              |
 * |-------------------------------------------------------------------------------------------|
 * |Approval odds     |  approval odds text    | Show text-based rec score & approval odds cta |
 * |-------------------------------------------------------------------------------------------|
 * |No approval odds  |  Show user's score     | Show text-based rec score & approval odds cta |
 * |-------------------------------------------------------------------------------------------|
 * @param   {Object}  offer                          The CC offer.
 * @param   {Object}  options
 * @param   {Object}  options.analytics              Analytics details for the AO impression.
 * @param   {boolean} options.approvalOddsEnabled    Flag to enabled or disable approvalOdds
 * @param   {Object}  options.identity               The identity object typically found in state
 *                                                   This is used to determine if the user is logged in
 *                                                   as well as the user's credit score
 * @param   {node}    options.ctaComponent           A custom CTA Component. MPC currently passes one
 *                                                   in for getQuickFactLikelihoodOfApproval()
 * @return  {Object}                                 The likelihood of approval text quick fact
 */

export var getQuickFactLikelihoodOfApprovalText = function getQuickFactLikelihoodOfApprovalText(offer) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var minCreditScoreIndex = getMinimumFICO(offer);
  var maxCreditScoreIndex = getMaximumFICO(offer);

  if (!(minCreditScoreIndex && maxCreditScoreIndex)) {
    return null;
  }

  var recommendedCreditScoreText = getRecommendedCreditScoreFormattedDetails(offer, maxCreditScoreIndex, minCreditScoreIndex);
  var approvalOdds = options.approvalOddsEnabled ? getApprovalOdds(offer) : null;
  var creditScore = getCreditScore(options);
  var analytics = options.analytics;
  var loaLabel = 'Recommended Credit Score';
  var loaLabelTooltip;
  var loaValue;
  var loaValueTooltip;

  if (approvalOdds && creditScore) {
    var approvalOddsText = getColorAndLabel({
      approvalOdds: approvalOdds
    }).label;
    loaValue = /*#__PURE__*/React.createElement(Impression, {
      className: styles.loaValueWrapper,
      entityName: "cc_approval_odds",
      eventType: "approval_odds_element_impression",
      payload: {
        section_name: analytics === null || analytics === void 0 ? void 0 : analytics.sectionName,
        section_position: analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition,
        additional_attributes: {
          approval_odds: approvalOddsText
        }
      }
    }, _capitalize(approvalOddsText));
    loaLabel = 'Your approval odds';
    loaValueTooltip = approvalOddsPopoverContent(approvalOdds);
  } else if (!approvalOdds && creditScore) {
    // note that @quick-fact-max-width is limiting the full extension of the loavalue for desktop in the current grid layout
    loaValue = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes('display-flex')
    }, /*#__PURE__*/React.createElement("div", null, creditScore), /*#__PURE__*/React.createElement("div", null, TRANSUNION_LOGO_CTA)), /*#__PURE__*/React.createElement("div", null, "(".concat(recommendedCreditScoreText.creditScoreRange, ")")));
    var creditScoreComparisonContent = getCreditScoreComparisonContent(creditScore, maxCreditScoreIndex, minCreditScoreIndex);
    loaLabelTooltip = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Type, {
      className: classes(['color-neutral-darkest', 'margin-bottom-2'])
    }, "You're", /*#__PURE__*/React.createElement("strong", null, " ".concat(creditScoreComparisonContent.pointDifference)), " ".concat(creditScoreComparisonContent.relativeScoreLabel, " suggested score of "), /*#__PURE__*/React.createElement("strong", null, creditScoreComparisonContent.scoreToCompare), "."), /*#__PURE__*/React.createElement(Type, {
      className: classes(['color-neutral-darkest'])
    }, DISCLAIMER_TEXT));
  } else {
    loaValue = /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes('display-flex--wide')
    }, /*#__PURE__*/React.createElement("div", {
      className: classes('margin-right-1')
    }, recommendedCreditScoreText.creditScoreRange), /*#__PURE__*/React.createElement("div", null, "(".concat(recommendedCreditScoreText.creditScoreRangeDescription, ")"))), /*#__PURE__*/React.createElement("div", null, options.ctaComponent));
  }

  return {
    id: 'likelihoodOfApproval',
    label: loaLabel,
    labelTooltip: loaLabelTooltip,
    value: !_isEmpty(offer) ? loaValue : null,
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: loaValueTooltip
  };
}; // exporting for the styleguide

export var WrappedApplyNow = function WrappedApplyNow(_ref) {
  var impressionId = _ref.impressionId,
      marketplaceEntity = _ref.marketplaceEntity,
      sectionName = _ref.sectionName,
      shouldLogImpression = _ref.shouldLogImpression,
      termsAndFeesLink = _ref.termsAndFeesLink;
  var impressionContext = useContext(ImpressionContext);
  return /*#__PURE__*/React.createElement(MarketplaceApplyNow, {
    className: styles.secondaryCta,
    finishType: APPLY_NOW_FINISH_TYPES.EXTERNAL_CONTENT,
    impressionId: impressionId !== null && impressionId !== void 0 ? impressionId : impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId,
    id: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
    link: termsAndFeesLink,
    linkType: APPLY_NOW_LINK_TYPES.ratesAndFeesText,
    marketplaceEntity: marketplaceEntity,
    monetizable: getMonetizable(marketplaceEntity),
    sectionName: sectionName,
    type: LINK_TYPES.LINK,
    shouldLogImpression: shouldLogImpression
  }, "Rates & Fees");
};

if (process.env.NODE_ENV !== 'production') {
  var PropTypes = require('prop-types'); // eslint-disable-line global-require


  WrappedApplyNow.propTypes = {
    impressionId: PropTypes.string,
    marketplaceEntity: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types
    sectionName: PropTypes.string,
    shouldLogImpression: PropTypes.bool,
    termsAndFeesLink: PropTypes.string
  };
}
/**
 * @todo - Create Structured Content compatible description and backport into `@nerdwallet/marketplace/drivers/credit-cards`.
 * @see {@link https://nerdwallet.atlassian.net/browse/RUR-171}
 */


export var getQuickFactRegularApr = function getQuickFactRegularApr(offer) {
  var _productImpressionPro;

  var includeRatesAndFeesLink = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var shouldLogImpression = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var productImpressionProps = arguments.length > 3 ? arguments[3] : undefined;
  var termsAndFeesLink = getTermsAndFeesLink(offer);
  return {
    id: 'regularApr',
    label: 'Regular APR',
    value: getOngoingAPR(offer),
    valueSize: VALUE_SIZES.MEDIUM,
    description: includeRatesAndFeesLink && termsAndFeesLink ? /*#__PURE__*/React.createElement(WrappedApplyNow, {
      impressionId: productImpressionProps === null || productImpressionProps === void 0 ? void 0 : productImpressionProps.productImpressionId,
      marketplaceEntity: offer,
      sectionName: productImpressionProps === null || productImpressionProps === void 0 ? void 0 : (_productImpressionPro = productImpressionProps.payload) === null || _productImpressionPro === void 0 ? void 0 : _productImpressionPro.section_name,
      shouldLogImpression: shouldLogImpression,
      termsAndFeesLink: termsAndFeesLink
    }) : null
  };
};
/**
 * @todo - Create Structured Content compatible value and backport into `@nerdwallet/marketplace/drivers/credit-cards`.
 * @see {@link https://nerdwallet.atlassian.net/browse/RUR-171}
 *
 * Get the star rating.
 * @param   {Object}  offer  The CC offer.
 * @return  {Object}  The star rating.
 */

export var getQuickFactStarRating = function getQuickFactStarRating(offer, starRatingProps) {
  if (!(starRatingProps !== null && starRatingProps !== void 0 && starRatingProps.showNotYetRated) && !(_isNumber(starRatingProps === null || starRatingProps === void 0 ? void 0 : starRatingProps.starRating) || _isNumber(getStarRating(offer)))) {
    return null;
  }

  var starRating = /*#__PURE__*/React.createElement(MarketplaceStarRating, _extends({
    marketplaceEntity: offer,
    layout: LAYOUTS.SMALL_NUMERIC,
    omitRatingNumberHref: false,
    hideRatingText: true,
    className: classNames(classes('display-inline-flex'), styles.starRatingValue)
  }, starRatingProps));
  return {
    id: 'starRating',
    label: 'NerdWallet rating',
    value: starRating,
    valueSize: VALUE_SIZES.MEDIUM,
    valueTooltip: TOOLTIP_TEXT.credit_cards
  };
};